import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetProfileQuery } from "../../../app/services/auth-rtk.service";
import {
  useGetBusinessPartnerQuery,
  useGetBusinessPartnerUsersStatusQuery
} from "../../../app/services/chatbot-rtk.services";
import LoadingComponent from "../../../components/common/loading/loading.component";
import { MultiLanguageUrl } from "../../../constants/multiLanguage.constant";
import InfoMessageAlert from "../../../features/bot/InfoMessageAlert";
import { PARTNER_LEVEL, PARTNER_TYPE } from "../../../utils/partnerObject";
import PartnerBaner from "./partnerBaner";
import PartnerPayout from "./partnerPayout";
import PartnerProfile from "./partnerProfile";
import PartnerUserStats from "./partnerUserStats";
import PartnerUserTable from "./partnerUserTable";
import { SumUserTypeEnum } from "../../../utils/SumUserTypeEnum";

interface IFormData {
  firstName: string;
  lastName: string;
  companyName: string;
  address1: string;
  address2?: string;
  city: string;
  country: string;
  zipcode: string;
  phone?: string;
  cookieConsent: boolean;
  birthDate: string;
}

const PartnerPanel = () => {
  const { t, i18n } = useTranslation();

  const { isLoading, data } = useGetBusinessPartnerQuery({});
  const businessPartnerUsersStatus = useGetBusinessPartnerUsersStatusQuery();
  const navigate = useNavigate();
  const profile = useGetProfileQuery();

  useEffect(() => {
    if (profile?.data?.user_type !== SumUserTypeEnum.BUSINESSPARTNER) {
      navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/`);
    }
  }, [profile?.data?.user_type, i18n.language]);

  const tabs = [
    <PartnerUserStats
      type={data?.partnerType ?? PARTNER_TYPE.AFFILIATE}
      level={data?.partnerLevel ?? PARTNER_LEVEL.STANDARD}
      monthlySales={businessPartnerUsersStatus?.data?.total}
      key={1}
    />,
    <PartnerUserTable key={2} />,
    <PartnerProfile profile={data} key={3} />,
    <PartnerPayout profile={data} key={4} />
  ];

  const [activeComponentIndex, setActiveComponentIndex] = useState(0);

  const handleClick = (index: number) => {
    setActiveComponentIndex(index);
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <React.Fragment>
      <PartnerBaner />
      {data?.isValidated === false && (
        <div className="tw-m-4">
          <InfoMessageAlert texts={[t("BusinessPartner/unlock-waiting")]} />
        </div>
      )}

      <div className="tw-flex tw-flex-col lg:tw-p-5 tw-gap-5">
        <div className="tw-flex tw-mt-5 tw-gap-x-5">
          <button
            onClick={() => handleClick(0)}
            className={`tw-relative cursor-pointer ${
              activeComponentIndex == 0
                ? "tw-text-blue-500"
                : "tw-text-gray-500"
            } tw-border-none tw-bg-transparent tw-text-sm tw-font-medium tw-font-['Lexend'] tw-leading-tight`}
          >
            {t("BusinessPartner/t-01")}
            {activeComponentIndex === 0 && (
              <div className="tw-absolute tw-w-[40px] tw-top-[38px]  tw-border-[1px] tw-border-blue-500 tw-border-solid"></div>
            )}
          </button>
          <button
            onClick={() => handleClick(1)}
            className={`tw-relative cursor-pointer ${
              activeComponentIndex == 1
                ? "tw-text-blue-500"
                : "tw-text-gray-500"
            } tw-border-none tw-bg-transparent tw-text-sm tw-font-medium tw-font-['Lexend'] tw-leading-tight`}
          >
            {t("BusinessPartner/t-02")}

            {activeComponentIndex === 1 && (
              <div className="tw-absolute tw-w-[40px] tw-top-[38px]  tw-border-[1px] tw-border-blue-500 tw-border-solid"></div>
            )}
          </button>
          <button
            onClick={() => handleClick(2)}
            className={`tw-relative cursor-pointer ${
              activeComponentIndex == 2
                ? "tw-text-blue-500"
                : "tw-text-gray-500"
            } tw-border-none tw-bg-transparent tw-text-sm tw-font-medium tw-font-['Lexend'] tw-leading-tight`}
          >
            {t("BusinessPartner/t-03")}

            {activeComponentIndex === 2 && (
              <div className="tw-absolute tw-w-[40px] tw-top-[38px]  tw-border-[1px] tw-border-blue-500 tw-border-solid"></div>
            )}
          </button>
          <button
            onClick={() => handleClick(3)}
            className={`tw-relative cursor-pointer ${
              activeComponentIndex == 3
                ? "tw-text-blue-500"
                : "tw-text-gray-500"
            } tw-border-none tw-bg-transparent tw-text-sm tw-font-medium tw-font-['Lexend'] tw-leading-tight`}
          >
            {t("BusinessPartner/t-04")}
            {activeComponentIndex === 3 && (
              <div className="tw-absolute tw-w-[40px] tw-top-[38px]  tw-border-[1px] tw-border-blue-500 tw-border-solid"></div>
            )}
          </button>
        </div>
        <div className="tw-w-full">
          <div className="tw-w-full tw-border-solid tw-border-gray-300 tw-h-[1px] tw-border-t-[1px] tw-border-b-0 "></div>
        </div>
        {tabs[activeComponentIndex]}
      </div>
    </React.Fragment>
  );
};

export default PartnerPanel;
